
import { useEffect, useRef, useCallback, useState } from "react";
import { io } from "socket.io-client";
import toast from "react-hot-toast";

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "";

const EVENTS = {
  // Client → Server
  JOIN_AUCTION:        "auction:join",
  LEAVE_AUCTION:       "auction:leave",
  PLACE_BID:           "auction:placeBid",
  REQUEST_LEADERBOARD: "auction:requestLeaderboard",

  // Server → Client
  AUCTION_JOINED:      "auction:joined",
  BID_RECEIVED:        "auction:bidReceived",
  BID_ACCEPTED:        "auction:bidAccepted",
  BID_REJECTED:        "auction:bidRejected",
  LEADERBOARD_UPDATE:  "auction:leaderboard",
  ITEM_LEADERBOARD:    "auction:itemLeaderboard",
  AUCTION_EXTENDED:    "auction:extended",
  AUCTION_ENDED:       "auction:ended",
  TIMER_SYNC:          "auction:timerSync",
  ERROR:               "auction:error",
};

const useAuctionSocket = (
  auctionId,
  userRole = "buyer",
  supplierCode = "",
  supplierName = ""
) => {
  const socketRef = useRef(null);

  const [isConnected,            setIsConnected]            = useState(false);
  const [socketError,            setSocketError]            = useState(null);
  const [liveBids,               setLiveBids]               = useState([]);
  const [liveBidRanking,         setLiveBidRanking]         = useState([]);
  const [serverRemainingSeconds, setServerRemainingSeconds] = useState(null);
  const [lastBidStatus,          setLastBidStatus]          = useState(null);
  const [auctionStatus,          setAuctionStatus]          = useState("active");
  const [onlineSuppliers,        setOnlineSuppliers]        = useState([]);
  const [auctionExtendedData,    setAuctionExtendedData]    = useState(null);

  // ── Per-item / config state 
  const [serverBidTimings,       setServerBidTimings]       = useState(null);
  const [itemLeaderboard,        setItemLeaderboard]        = useState([]);
  const [itemMbd,                setItemMbd]                = useState({});
  const [itemReservePrice,       setItemReservePrice]       = useState({});
  const [firstQuote,             setFirstQuote]             = useState(false);
  const [firstQuoteBy,           setFirstQuoteBy]           = useState("Supplier");
  const [auctionType,            setAuctionType]            = useState("Reverse");
  const [supplierInitialPrices,  setSupplierInitialPrices]  = useState({});

  //  NEW: bidAgainst  "leadingPrice" | "ownPrice" 
  // Received from server on auction:joined so all clients stay in sync
  const [bidAgainst,             setBidAgainst]             = useState("leadingPrice");

  const resolvedAuctionId =
    auctionId || sessionStorage.getItem("activeAuctionId") || "";

  useEffect(() => {
    if (auctionId) sessionStorage.setItem("activeAuctionId", auctionId);
  }, [auctionId]);

  useEffect(() => {
    if (!resolvedAuctionId) return;

    let localData = localStorage.getItem("userData");
    let token = "";
    try {
      token = localData ? JSON.parse(localData)?.token || "" : "";
    } catch {
      token = "";
    }

    const socket = io(SOCKET_URL, {
      transports: ["websocket"],
      auth: { token },
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    // ── Connection 
    socket.on("connect", () => {
      setIsConnected(true);
      setSocketError(null);
      socket.emit(EVENTS.JOIN_AUCTION, {
        auctionId: resolvedAuctionId,
        userRole,
        supplierCode,
        supplierName,
      });
    });

    socket.on("disconnect", () => setIsConnected(false));

    socket.on("connect_error", (err) => {
      setSocketError(err?.message || "Socket connection failed");
      setIsConnected(false);
    });

    // ── auction:joined 
    socket.on(EVENTS.AUCTION_JOINED, (data) => {
      if (data?.leaderboard)              setLiveBidRanking(data.leaderboard);
      if (data?.bids)                     setLiveBids(data.bids);
      if (data?.remainingSeconds != null) setServerRemainingSeconds(data.remainingSeconds);
      if (data?.auctionStatus)            setAuctionStatus(data.auctionStatus);
      if (data?.onlineSuppliers)          setOnlineSuppliers(data.onlineSuppliers);

      if (data?.bidTimings && Object.keys(data.bidTimings).length > 0) {
        setServerBidTimings(data.bidTimings);
      }

      if (data?.itemLeaderboard)          setItemLeaderboard(data.itemLeaderboard);
      if (data?.itemMbd)                  setItemMbd(data.itemMbd);
      if (data?.itemReservePrice)         setItemReservePrice(data.itemReservePrice);
      if (data?.firstQuote != null)       setFirstQuote(data.firstQuote);
      if (data?.firstQuoteBy)             setFirstQuoteBy(data.firstQuoteBy);
      if (data?.auctionType)              setAuctionType(data.auctionType);
      if (data?.supplierInitialPrices)    setSupplierInitialPrices(data.supplierInitialPrices);

      // ── NEW: sync bidAgainst from server 
      if (data?.bidAgainst)               setBidAgainst(data.bidAgainst);
    });

    // ── auction:bidReceived 
    socket.on(EVENTS.BID_RECEIVED, (bidData) => {
      setLiveBids((prev) => [bidData, ...prev].slice(0, 100));
      toast.success(`New bid from ${bidData?.supplierName || "a supplier"}`, {
        duration: 3000,
        position: "top-right",
        icon: "📥",
      });
    });

    // ── auction:bidAccepted 
    socket.on(EVENTS.BID_ACCEPTED, (data) => {
      setLastBidStatus({
        status: "accepted",
        message: data?.message || "Bid placed successfully!",
        bidData: data,
      });
      toast.success(data?.message || "Your bid was accepted!", {
        duration: 4000,
        position: "top-right",
      });
    });

    // ── auction:bidRejected 
    socket.on(EVENTS.BID_REJECTED, (data) => {
      setLastBidStatus({
        status: "rejected",
        message: data?.message || "Bid rejected",
        bidData: data,
      });
      toast.error(
        data?.message || "Your bid was rejected. Please enter a valid amount.",
        { duration: 5000, position: "top-right" }
      );
    });

    // ── auction:leaderboard (overall) 
    socket.on(EVENTS.LEADERBOARD_UPDATE, (data) => {
      if (data?.leaderboard) setLiveBidRanking(data.leaderboard);
      if (data?.bids)        setLiveBids(data.bids);
    });

    // ── auction:itemLeaderboard (per-item rankings) 
    socket.on(EVENTS.ITEM_LEADERBOARD, (data) => {
      if (data?.itemLeaderboard) {
        setItemLeaderboard(data.itemLeaderboard);
      }
    });

    // ── auction:timerSync 
    socket.on(EVENTS.TIMER_SYNC, (data) => {
      if (data?.remainingSeconds != null)
        setServerRemainingSeconds(data.remainingSeconds);
    });

    // ── auction:extended 
    socket.on(EVENTS.AUCTION_EXTENDED, (data) => {
      setAuctionStatus("extended");
      if (data?.remainingSeconds != null)
        setServerRemainingSeconds(data.remainingSeconds);

      if (
        data?.updatedBidTimings &&
        Object.keys(data.updatedBidTimings).length > 0
      ) {
        setServerBidTimings(data.updatedBidTimings);
      }

      setAuctionExtendedData({
        extendedByMs:      (parseFloat(data?.extensionMinutes) || 0) * 60 * 1000,
        newEndTime:        data?.newClosingTime || null,
        cyclesUsed:        data?.cyclesUsed,
        maxCycles:         data?.maxCycles,
        bidConfigType:     data?.bidConfigType,
        updatedBidTimings: data?.updatedBidTimings || null,
      });

      const bidConfig = data?.bidConfigType || "parallel";
      const msg =
        bidConfig === "serial"
          ? `⏱ Active lot extended by ${data?.extensionMinutes || ""} min! Subsequent lots cascaded. (Cycle ${data?.cyclesUsed}/${data?.maxCycles})`
          : bidConfig === "staggered"
          ? `⏱ Current item extended by ${data?.extensionMinutes || ""} min! Subsequent items cascaded. (Cycle ${data?.cyclesUsed}/${data?.maxCycles})`
          : `⏱ Auction extended by ${data?.extensionMinutes || ""} min! New closing: ${data?.newClosingTime || ""} (Cycle ${data?.cyclesUsed}/${data?.maxCycles})`;

      toast(msg, {
        duration: 6000,
        position: "top-right",
        style: { background: "#f59e0b", color: "#fff" },
      });
    });

    // ── auction:ended 
    socket.on(EVENTS.AUCTION_ENDED, (data) => {
      setAuctionStatus("ended");
      toast(`🏁 Auction has ended!`, {
        duration: 6000,
        position: "top-right",
        style: { background: "#374151", color: "#fff" },
      });
      if (data?.leaderboard) setLiveBidRanking(data.leaderboard);
    });

    // ── online suppliers 
    socket.on("auction:onlineSuppliers", (data) => {
      if (data?.onlineSuppliers) setOnlineSuppliers(data.onlineSuppliers);
    });

    // ── error 
    socket.on(EVENTS.ERROR, (data) => {
      setSocketError(data?.message || "Auction socket error");
      toast.error(data?.message || "Auction error occurred", {
        duration: 4000,
        position: "top-right",
      });
    });

    return () => {
      socket.emit(EVENTS.LEAVE_AUCTION, { auctionId: resolvedAuctionId });
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAuctionId]);

  // ── Emit: Place Bid 
  const emitPlaceBid = useCallback(
    (bidPayload) => {
      if (!socketRef.current?.connected) {
        toast.error("Not connected to auction server. Please refresh.", {
          position: "top-right",
        });
        return false;
      }
      socketRef.current.emit(EVENTS.PLACE_BID, {
        ...bidPayload,
        auctionId: resolvedAuctionId,
      });
      setLastBidStatus(null);
      return true;
    },
    // eslint-disable-next-line
    [auctionId]
  );

  // ── Emit: Join/Rejoin 
  const emitAuctionJoin = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(EVENTS.JOIN_AUCTION, {
        auctionId: resolvedAuctionId,
        userRole,
      });
    }
    // eslint-disable-next-line
  }, [auctionId, userRole]);

  //  Emit: Request fresh leaderboard 
  const emitRequestLeaderboard = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(EVENTS.REQUEST_LEADERBOARD, {
        auctionId: resolvedAuctionId,
      });
    }
    // eslint-disable-next-line
  }, [auctionId]);

  return {
    isConnected,
    socketError,
    liveBids,
    liveBidRanking,
    serverRemainingSeconds,
    lastBidStatus,
    auctionStatus,
    onlineSuppliers,
    emitPlaceBid,
    emitAuctionJoin,
    emitRequestLeaderboard,
    auctionExtendedData,
    serverBidTimings,
    itemLeaderboard,
    itemMbd,
    itemReservePrice,
    firstQuote,
    firstQuoteBy,
    auctionType,
    supplierInitialPrices,
    //  NEW  fo bidagainst event
    bidAgainst,
    EVENTS,
  };
};

export default useAuctionSocket;
